import { storeMoney, type MerchOrderView } from "@libs/platform-wire";

/**
 * A merch order: what a fan bought from an artist's store on Lissen, which
 * the store posts to them (or, for a digital download, emails: Lissen sends
 * its email on). This module is the presentation the app's wallet
 * row and order screen and Link in Bio's merch page share: every sentence,
 * so each says the same thing. No QR, no "tickets arrive": nothing is
 * collected at a door. It decides nothing about who may see an order: the
 * server sends a holder only their own, and the address only on the
 * order's own read.
 */

export const MERCH_ORDER_COPY = {
  title: "My order",
  /** The access page's button that opens it. */
  open: "My order",
  ordered: "Ordered",
  items: "What you bought",
  delivery: "Delivery address",
  shipped: "Shipped",
  /** A digital download's state, in place of who posts it where. */
  downloadWaiting: "Waiting for the store's email",
  downloadEmailed: "Emailed to you",
  /** The button that opens a download's link. */
  download: "Download",
  total: "Total",
  loading: "Loading...",
  notFound: "No order found",
  help: "Questions about your order? Contact us and we'll help.",
  contact: "Contact",
} as const;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** The day an order was placed, on the UK calendar the store posts on:
 *  `{ day: 14, month: "October", year: 2026 }`. */
function orderDay(orderedAt: string) {
  const instant = new Date(orderedAt);
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      day: "numeric",
      month: "numeric",
      year: "numeric",
    }).formatToParts(instant);
    const part = (type: string) =>
      Number(parts.find((each) => each.type === type)?.value);
    const [day, month, year] = [part("day"), part("month"), part("year")];
    if (day && month && year) return { day, month: MONTHS[month - 1]!, year };
  } catch {
    // A runtime without time zones: the UTC day below.
  }
  return {
    day: instant.getUTCDate(),
    month: MONTHS[instant.getUTCMonth()]!,
    year: instant.getUTCFullYear(),
  };
}

/** "14 October 2026". */
export function merchOrderDate(orderedAt: string) {
  const { day, month, year } = orderDay(orderedAt);
  return `${day} ${month} ${year}`;
}

/** "23 October 2026, 7:45pm": when something happened to an order, on the
 *  UK clock, as `merchOrderDate` reads its day. */
export function merchOrderDateTime(at: string) {
  const instant = new Date(at);
  let time: string | null = null;
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).formatToParts(instant);
    const part = (type: string) =>
      parts.find((each) => each.type === type)?.value ?? "";
    const hour = part("hour");
    const period = part("dayPeriod").replace(/\./g, "").toLowerCase();
    if (hour && period) time = `${hour}:${part("minute")}${period}`;
  } catch {
    // A runtime without time zones: the UTC time below.
  }
  if (!time) {
    const hours = instant.getUTCHours();
    time = `${hours % 12 || 12}:${String(instant.getUTCMinutes()).padStart(2, "0")}${
      hours < 12 ? "am" : "pm"
    }`;
  }
  return `${merchOrderDate(at)}, ${time}`;
}

/**
 * A digital download's state, in place of who posts it where: waiting for
 * the store's email ("We'll send it to {email}"), or emailed on to the fan
 * (when), with the download's own link once
 * the order's own read has it. Null for posted merch.
 */
export function merchOrderDownload(
  order: Pick<MerchOrderView, "download">,
  email?: string | null,
) {
  const download = order.download;
  if (!download) return null;
  const to = email?.trim() || "your email";
  return {
    title: download.emailedAt
      ? MERCH_ORDER_COPY.downloadEmailed
      : MERCH_ORDER_COPY.downloadWaiting,
    detail: download.emailedAt
      ? // The first mail sent on can be the store's order confirmation,
        // under its own subject: no subject is promised.
        `${merchOrderDateTime(download.emailedAt)}. We sent the store's email on to you.`
      : `The store emails the download. We'll send it to ${to}.`,
    /** The download's own link: only on the order's own read, once the
     *  store's email gave one. */
    url: download.url,
  };
}

/** "1 × Size L", or "2 × Tour Poster" for a product with nothing to
 *  choose: each line of what was bought, as the order confirmation lists
 *  them. */
export function merchOrderItems(
  order: Pick<MerchOrderView, "items" | "optionLabel">,
  productTitle: string,
) {
  return order.items.map((item, index) => ({
    key: `${item.option ?? ""}:${index}`,
    text: `${item.quantity} × ${
      item.option
        ? `${order.optionLabel ? `${order.optionLabel} ` : ""}${item.option}`
        : productTitle
    }`,
  }));
}

const money = storeMoney;

/** A country as people read it ("United Kingdom" for "GB"). */
function countryName(code: string) {
  try {
    const name = new Intl.DisplayNames(["en-GB"], { type: "region" }).of(code);
    if (name && name !== code) return name;
  } catch {
    // A runtime without display names (Hermes): the stores post to the UK.
  }
  return code === "GB" ? "United Kingdom" : code;
}

/**
 * A wallet row's lines for a product the fan ordered: what they bought
 * ("1 × Size L, 2 × Size XL") and when ("Ordered 14 Oct"), from their
 * newest order, saying how many more there are.
 */
export function createMerchOrderRowModel(
  orders: readonly MerchOrderView[],
  productTitle: string,
) {
  const [newest] = [...orders].sort((a, b) =>
    b.orderedAt.localeCompare(a.orderedAt),
  );
  if (!newest) return null;
  const { day, month } = orderDay(newest.orderedAt);
  const more = orders.length - 1;
  return {
    items: merchOrderItems(newest, productTitle)
      .map((item) => item.text)
      .join(", "),
    ordered: `${MERCH_ORDER_COPY.ordered} ${day} ${month.slice(0, 3)}${
      more > 0 ? ` · ${more + 1} orders` : ""
    }`,
  };
}

/** "14 October 2026 by Remedy Fulfilment": when an order shipped and who
 *  sent it, in the store's words; null while it has not shipped. */
export function merchOrderShipped(order: Pick<MerchOrderView, "shipped">) {
  const shipped = order.shipped;
  if (!shipped) return null;
  const by = shipped.by?.trim();
  return `${merchOrderDate(shipped.at)}${by ? ` by ${by}` : ""}`;
}

/**
 * One order as its screen shows it: when it was ordered, what was bought,
 * who posts it to where (or, for a digital download, its state and
 * Download button), when it shipped, the store's order reference and
 * the total. The store is named as the order's `seller` names it, from the
 * server. `email` is the fan's Lissen email, which a download is sent on
 * to ("your email" without it).
 */
export function createMerchOrderModel(
  order: MerchOrderView,
  productTitle: string,
  options: { email?: string | null } = {},
) {
  const download = merchOrderDownload(order, options.email);
  // A download's address is its card's billing address, never a delivery.
  const address = download ? null : order.deliveryAddress;
  return {
    key: order.id,
    ordered: merchOrderDate(order.orderedAt),
    items: merchOrderItems(order, productTitle),
    /** "Music Glue posts it to you"; "The store posts it to you". */
    postedBy: `${order.seller.name} posts it to you`,
    /** A digital download's state, shown in place of `postedBy` and the
     *  address; null for posted merch. */
    download,
    /** "14 October 2026 by Remedy Fulfilment", under "Shipped"; null
     *  while it has not shipped. */
    shipped: merchOrderShipped(order),
    /** Null on a list read, which never carries the address. */
    addressLines: address
      ? [
          address.name,
          address.line1,
          address.city,
          address.postcode,
          countryName(address.country),
        ].filter((line) => line.trim().length > 0)
      : null,
    reference: order.orderReference
      ? {
          /** "Music Glue order ID"; "Store order number". */
          label: order.seller.referenceLabel,
          value: order.orderReference,
        }
      : null,
    total: order.total ? money(order.total.amount, order.total.currency) : null,
  };
}

export type MerchOrderModel = ReturnType<typeof createMerchOrderModel>;
